// File: frontend/src/pages/Leads/LeadsPage.jsx
// Main leads management page for WA Contact Center CRM

import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { useQuery } from 'react-query';
import { useAuth } from '../../contexts/AuthContext';
import toast from 'react-hot-toast';
import api from '../../services/api';
import LeadsTable from '../../components/Leads/LeadsTable';
import LeadFilters from '../../components/Leads/LeadFilters';
import CreateLeadModal from '../../components/Leads/CreateLeadModal';
import { TagsFilter } from '../../components/Tags/TagManager';
import { useAvailableTags } from '../../hooks/useTags';
import {
  PlusIcon,
  FunnelIcon,
  ArrowDownTrayIcon,
  MagnifyingGlassIcon,
} from '@heroicons/react/24/outline';

const LeadsPage = () => {
  const { user, hasPermission } = useAuth();
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showFilters, setShowFilters] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedTags, setSelectedTags] = useState([]);
  const [filters, setFilters] = useState({
    page: 1,
    limit: 25,
    lifecycleStage: '',
    leadStatus: '',
    minScore: '',
    maxScore: '',
    ownerId: '',
    leadSource: '',
    createdFrom: '',
    noContactDays: '',
    archivedOnly: false,
    sortBy: 'created_at',
    sortOrder: 'DESC',
  });

  // Fetch available tags for filtering
  const { tags: availableTags, loading: tagsLoading } = useAvailableTags();

  // Fetch leads data
  const {
    data: leadsData,
    isLoading,
    error,
    refetch,
  } = useQuery(
    ['leads', { ...filters, search: searchTerm, tags: selectedTags }],
    async () => {
      const params = new URLSearchParams();
      Object.entries({ ...filters, search: searchTerm }).forEach(([k, v]) => {
        if (k === 'archivedOnly') {
          if (v) params.append('archivedOnly', 'true');
          return;
        }
        if (v !== '' && v !== null && v !== undefined) params.append(k, v);
      });
      if (selectedTags.length > 0) params.append('tags', selectedTags.join(','));

      const response = await api.get(`/leads?${params}`);
      return response.data;
    },
    {
      keepPreviousData: true,
      refetchInterval: 30000, // Refresh every 30 seconds for real-time updates
    }
  );

  // Fetch leads stats for KPIs
  const {
    data: statsData,
    isLoading: statsLoading,
  } = useQuery(
    'leads-stats',
    async () => {
      const response = await api.get('/leads/stats/dashboard');
      return response.data.data;
    },
    {
      refetchInterval: 60000, // Refresh stats every minute
    }
  );

  // Handle filter changes
  const handleFilterChange = (newFilters) => {
    setFilters(prev => ({
      ...prev,
      ...newFilters,
      page: 1, // Reset to first page when filters change
    }));
  };

  // Handle pagination
  const handlePageChange = (page) => {
    setFilters(prev => ({ ...prev, page }));
  };

  // Handle search
  const handleSearch = (term) => {
    setSearchTerm(term);
    setFilters(prev => ({ ...prev, page: 1 }));
  };

  // Handle tag filter changes
  const handleTagsChange = (tags) => {
    setSelectedTags(tags);
    setFilters(prev => ({ ...prev, page: 1 }));
  };

  // Archivar o desarchivar un lead (según la vista actual) y refrescar la lista.
  const handleArchive = async (lead) => {
    const archiving = !filters.archivedOnly;
    try {
      await api.patch(`/leads/${lead.id}/${archiving ? 'archive' : 'unarchive'}`);
      toast.success(archiving ? 'Lead arquivado' : 'Lead desarquivado');
      refetch();
    } catch (error) {
      toast.error(error.response?.data?.error || 'Não foi possível atualizar o lead');
    }
  };

  // Export leads function
  const handleExport = async () => {
    try {
      const params = new URLSearchParams({
        ...filters,
        search: searchTerm,
        export: 'csv',
      });

      // Add tags to export if selected
      if (selectedTags.length > 0) {
        params.append('tags', selectedTags.join(','));
      }

      const response = await api.get(`/leads/export?${params}`, {
        responseType: 'blob',
      });

      // Create download link
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `leads-${new Date().toISOString().split('T')[0]}.csv`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success('Exportação concluída');
    } catch (error) {
      console.error('Export error:', error);
      toast.error(
        error.response?.data?.error || 'Não foi possível exportar os leads. Tente novamente.'
      );
    }
  };

  if (error) {
    return (
      <div className="p-4 sm:p-6">
        <div className="bg-red-50 border border-red-200 rounded-md p-4">
          <h3 className="text-sm font-medium text-red-800">
            Erro ao carregar leads
          </h3>
          <p className="text-sm text-red-700 mt-1">
            {error.response?.data?.error || 'Erro desconhecido'}
          </p>
          <button
            onClick={() => refetch()}
            className="mt-3 text-sm bg-red-100 text-red-800 px-3 py-1 rounded-md hover:bg-red-200"
          >
            Tentar novamente
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="p-4 sm:p-6 max-w-7xl mx-auto">
      {/* Page header */}
      <div className="mb-6">
        <div className="flex flex-col gap-4 sm:flex-row sm:justify-between sm:items-center">
          <div>
            <h1 className="text-3xl font-bold text-gray-900 dark:text-gray-100">Leads</h1>
            <p className="text-gray-600 dark:text-gray-300 mt-2">
              Gerencie e qualifique seus leads para maximizar conversões
            </p>
          </div>

          <div className="flex items-center gap-3">
            {/* Export button */}
            <button
              onClick={handleExport}
              className="inline-flex items-center px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white dark:bg-gray-800 hover:bg-gray-50 dark:bg-gray-950"
            >
              <ArrowDownTrayIcon className="h-5 w-5 mr-2" />
              Exportar
            </button>

            {/* Create lead button */}
            {hasPermission('leads', 'create') && (
              <button
                onClick={() => setShowCreateModal(true)}
                className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700"
              >
                <PlusIcon className="h-5 w-5 mr-2" />
                Novo Lead
              </button>
            )}
          </div>
        </div>

        {/* Stats cards */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mt-6">
          <div className="bg-white dark:bg-gray-800 p-4 rounded-lg shadow border">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <div className="w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center">
                  <span className="text-blue-600 font-semibold text-sm">T</span>
                </div>
              </div>
              <div className="ml-3">
                <p className="text-sm font-medium text-gray-500 dark:text-gray-400">Total Leads</p>
                <p className="text-lg font-semibold text-gray-900 dark:text-gray-100">
                  {statsLoading ? '...' : (leadsData?.pagination?.total_items || statsData?.total_leads || 0)}
                </p>
              </div>
            </div>
          </div>

          <div className="bg-white dark:bg-gray-800 p-4 rounded-lg shadow border">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <div className="w-8 h-8 bg-green-100 rounded-full flex items-center justify-center">
                  <span className="text-green-600 font-semibold text-sm">Q</span>
                </div>
              </div>
              <div className="ml-3">
                <p className="text-sm font-medium text-gray-500 dark:text-gray-400">Qualificados</p>
                <p className="text-lg font-semibold text-gray-900 dark:text-gray-100">
                  {statsLoading ? '...' : (statsData?.mqls || 0)}
                </p>
              </div>
            </div>
          </div>

          <div className="bg-white dark:bg-gray-800 p-4 rounded-lg shadow border">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <div className="w-8 h-8 bg-yellow-100 rounded-full flex items-center justify-center">
                  <span className="text-yellow-600 font-semibold text-sm">S</span>
                </div>
              </div>
              <div className="ml-3">
                <p className="text-sm font-medium text-gray-500 dark:text-gray-400">Score Médio</p>
                <p className="text-lg font-semibold text-gray-900 dark:text-gray-100">
                  {statsLoading ? '...' : (statsData?.avg_lead_score || 0)}
                </p>
              </div>
            </div>
          </div>

          <div className="bg-white dark:bg-gray-800 p-4 rounded-lg shadow border">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <div className="w-8 h-8 bg-purple-100 rounded-full flex items-center justify-center">
                  <span className="text-purple-600 font-semibold text-sm">C</span>
                </div>
              </div>
              <div className="ml-3">
                <p className="text-sm font-medium text-gray-500 dark:text-gray-400">Taxa Conversão</p>
                <p className="text-lg font-semibold text-gray-900 dark:text-gray-100">
                  {statsLoading ? '...' : `${statsData?.conversion_rate_30d || 0}%`}
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Search and filters */}
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow mb-6">
        <div className="p-4 border-b border-gray-200 dark:border-gray-700">
          <div className="flex flex-col md:flex-row md:items-center md:justify-between space-y-3 md:space-y-0">
            {/* Search bar */}
            <div className="flex-1 max-w-lg">
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <MagnifyingGlassIcon className="h-5 w-5 text-gray-400" />
                </div>
                <input
                  type="text"
                  placeholder="Buscar por nome, email, empresa..."
                  value={searchTerm}
                  onChange={(e) => handleSearch(e.target.value)}
                  className="block w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md leading-5 bg-white dark:bg-gray-800 placeholder-gray-500 focus:outline-none focus:placeholder-gray-400 focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
                />
              </div>
            </div>

            {/* Filter toggle */}
            <button
              onClick={() => setShowFilters(!showFilters)}
              className={`inline-flex items-center px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium ${
                showFilters 
                  ? 'text-blue-700 bg-blue-50 border-blue-300' 
                  : 'text-gray-700 bg-white dark:bg-gray-800 hover:bg-gray-50 dark:bg-gray-950'
              }`}
            >
              <FunnelIcon className="h-5 w-5 mr-2" />
              Filtros
              {(filters.lifecycleStage || filters.leadStatus || filters.minScore || filters.maxScore
                || filters.ownerId || filters.leadSource || filters.createdFrom || filters.noContactDays
                || filters.archivedOnly || selectedTags.length > 0) && (
                <span className="ml-2 inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                  Ativos
                </span>
              )}
            </button>
          </div>
        </div>

        {/* Filters panel */}
        {showFilters && (
          <div className="p-4 border-b border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-950 space-y-4">
            {/* Tags filter */}
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                🏷️ Filtrar por Tags
              </label>
              <TagsFilter
                selectedTags={selectedTags}
                onTagsChange={handleTagsChange}
                availableTags={availableTags}
                placeholder="Selecionar tags para filtrar..."
                multiple={true}
              />
            </div>
            
            {/* Other filters */}
            <LeadFilters
              filters={filters}
              onFilterChange={handleFilterChange}
              userRole={user?.role?.name}
            />
          </div>
        )}

        {/* Leads table */}
        <div>
          {isLoading ? (
            <div className="flex items-center justify-center h-64">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
            </div>
          ) : (
            <LeadsTable
              leads={leadsData?.data || []}
              pagination={leadsData?.pagination}
              onPageChange={handlePageChange}
              onSort={handleFilterChange}
              currentSort={{ field: filters.sortBy, order: filters.sortOrder }}
              userRole={user?.role?.name}
              userPermissions={user?.permissions}
              onArchive={handleArchive}
              archivedView={!!filters.archivedOnly}
            />
          )}
        </div>
      </div>

      {/* Quick tips for Francielle */}
      {user?.role?.name === 'Especialista_Leads' && (
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-6">
          <h3 className="text-lg font-semibold text-blue-900 mb-3">
            💡 Dicas para Qualificação de Leads
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm text-blue-800">
            <div>
              <h4 className="font-medium mb-2">Leads com Score 7+:</h4>
              <p>Contate imediatamente - alta probabilidade de conversão</p>
            </div>
            <div>
              <h4 className="font-medium mb-2">Leads com Score 4-6:</h4>
              <p>Qualifique primeiro - entenda a necessidade</p>
            </div>
            <div>
              <h4 className="font-medium mb-2">Leads com Score &lt;4:</h4>
              <p>Nutra com conteúdo antes de abordar</p>
            </div>
            <div>
              <h4 className="font-medium mb-2">Transferir para Rui:</h4>
              <p>Quando lead demonstrar interesse real e orçamento</p>
            </div>
          </div>
        </div>
      )}

      {/* Create Lead Modal */}
      {showCreateModal && (
        <CreateLeadModal
          isOpen={showCreateModal}
          onClose={() => setShowCreateModal(false)}
          onSuccess={() => {
            setShowCreateModal(false);
            refetch();
          }}
        />
      )}
    </div>
  );
};

export default LeadsPage;